import React, { useEffect } from 'react';

interface ChatAutomationProps {
  websiteId: string;
}

const ChatAutomation: React.FC<ChatAutomationProps> = ({ websiteId }) => {
  useEffect(() => {
    // Wait for Crisp to load
    const setupAutomation = () => {
      if (window.$crisp) {
        // Set up automated scenarios and responses
        setupCommonQuestions();
        setupWelcomeMessage();
        setupOfflineMessage();
        setupQuickReplies();
      }
    };

    // If Crisp is already loaded, set up immediately
    if (window.$crisp) {
      setupAutomation();
    } else {
      // Otherwise, wait for it to load
      const checkCrisp = setInterval(() => {
        if (window.$crisp) {
          setupAutomation();
          clearInterval(checkCrisp);
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkCrisp), 10000);
    }
  }, [websiteId]);

  const setupCommonQuestions = () => {
    // Set up chatbot scenarios for common questions
    window.$crisp.push(['set', 'session:data', ['scenarios', [
      {
        id: 'welcome',
        name: 'Welcome Message',
        trigger: 'visitor_first_message',
        actions: [
          {
            type: 'text',
            content: "سلام! Welcome to Farsi Learner! 👋\n\nI'm here to help you with your Persian learning journey. Here are some quick options:"
          },
          {
            type: 'quick_replies',
            content: 'What can I help you with?',
            replies: [
              { title: '🎓 Getting Started', payload: 'getting_started' },
              { title: '🔊 Audio Issues', payload: 'audio_help' },
              { title: '📚 Lessons', payload: 'lesson_help' },
              { title: '💳 Subscription', payload: 'subscription_help' },
              { title: '📱 Technical Support', payload: 'tech_support' },
              { title: '💬 Speak to Human', payload: 'human_support' }
            ]
          }
        ]
      },
      {
        id: 'getting_started',
        name: 'Getting Started Help',
        trigger: 'quick_reply:getting_started',
        actions: [
          {
            type: 'text',
            content: "🎓 **Getting Started with Farsi Learner**\n\n1. **Start with the Alphabet**: Begin with our Grid Alphabet lessons to learn Persian letters\n2. **Try Vocabulary**: Explore our vocabulary categories like Kitchen, Family, etc.\n3. **Practice Daily**: Use our flashcards and quizzes for daily practice\n4. **Set Goals**: Track your progress in the Progress page\n\n✨ **Pro Tip**: Listen to audio pronunciations carefully - Persian pronunciation is key to fluency!\n\nWould you like help with any specific lesson or feature?"
          }
        ]
      },
      {
        id: 'audio_help',
        name: 'Audio Issues Help',
        trigger: 'quick_reply:audio_help',
        actions: [
          {
            type: 'text',
            content: "🔊 **Audio Troubleshooting**\n\n**Common Solutions:**\n• Check your device volume and unmute\n• Try refreshing the page\n• Clear your browser cache\n• Try a different browser (Chrome recommended)\n• Check your internet connection\n\n**Still having issues?**\n• Which lesson/page has audio problems?\n• What device are you using?\n• What browser are you using?\n\nLet me know and I'll get our tech team to help!"
          }
        ]
      },
      {
        id: 'lesson_help',
        name: 'Lesson Help',
        trigger: 'quick_reply:lesson_help',
        actions: [
          {
            type: 'text',
            content: "📚 **Lesson Help**\n\n**Available Learning Paths:**\n🔤 **Grid Alphabet**: Learn Persian letters and pronunciation\n🏠 **Vocabulary Categories**: Kitchen, Family, Numbers, etc.\n📖 **Grammar Lessons**: Sentence structure and grammar rules\n🌙 **Persian Nights**: Cultural stories and context\n\n**Study Features:**\n• 🎴 Flashcards for memorization\n• 🧠 Quizzes to test knowledge\n• 🎯 Progress tracking\n• 🔊 Audio pronunciation guides\n\nWhich lesson type interests you most?"
          }
        ]
      },
      {
        id: 'subscription_help',
        name: 'Subscription Help',
        trigger: 'quick_reply:subscription_help',
        actions: [
          {
            type: 'text',
            content: "💳 **Subscription & Billing**\n\n**Free Features:**\n• Basic alphabet lessons\n• Limited vocabulary\n• Sample audio content\n\n**Premium Features:**\n• Full lesson library\n• All audio pronunciations\n• Progress tracking\n• Flashcards & quizzes\n• Priority support\n\n**Need Help With:**\n• Upgrading to premium?\n• Billing questions?\n• Payment issues?\n• Cancellation?\n\nJust let me know what you need!"
          }
        ]
      },
      {
        id: 'tech_support',
        name: 'Technical Support',
        trigger: 'quick_reply:tech_support',
        actions: [
          {
            type: 'text',
            content: "📱 **Technical Support**\n\n**Common Issues & Solutions:**\n\n🔄 **Page Loading**: Try refreshing or clearing cache\n📱 **Mobile Issues**: Use Chrome or Safari for best experience\n🔐 **Login Problems**: Check email/password or reset password\n💾 **Progress Not Saving**: Ensure you're logged in\n🎵 **Audio Not Playing**: Check volume, try different browser\n\n**Browser Recommendations:**\n✅ Chrome (recommended)\n✅ Safari\n✅ Firefox\n❌ Internet Explorer (not supported)\n\nWhat specific issue are you experiencing?"
          }
        ]
      },
      {
        id: 'human_support',
        name: 'Human Support Request',
        trigger: 'quick_reply:human_support',
        actions: [
          {
            type: 'text',
            content: "👨‍💼 **Connecting you to our support team...**\n\nA human team member will be with you shortly! In the meantime:\n\n📧 **Email**: support@pingquack.com\n⏰ **Response Time**: Usually within 24 hours\n🕐 **Best Times**: Weekdays 9 AM - 5 PM PST\n\nTo help us assist you faster, please share:\n• What you're trying to do\n• What's not working\n• Your device/browser info\n• Any error messages you see\n\nThank you for your patience! 🙏"
          }
        ]
      }
    ]]]);

    // Set up keyword-based auto-responses
    setupKeywordResponses();
  };

  const setupKeywordResponses = () => {
    // Common Persian/Farsi learning keywords
    const keywordResponses = {
      'pronunciation': "🗣️ **Pronunciation Help**: Each lesson includes audio by native speakers. Click the 🔊 icon to hear correct pronunciation. Practice speaking along!",
      'alphabet': "🔤 **Persian Alphabet**: Start with our Grid Alphabet lessons. Persian has 32 letters with different forms. Begin with 'Alef' lesson!",
      'grammar': "📖 **Grammar Help**: Check our Grammar section for sentence structure, verb conjugations, and language rules with examples.",
      'vocabulary': "📚 **Vocabulary**: Explore categories like Kitchen (آشپزخانه), Family (خانواده), Numbers (اعداد), and more. Each has audio and flashcards!",
      'audio': "🔊 **Audio Issues**: Try refreshing the page, check volume, or use Chrome browser. Still having problems? Let me know which lesson!",
      'login': "🔐 **Login Help**: Use your email and password. Forgot password? Use 'Reset Password' link. Need to create account? Click 'Sign Up'.",
      'subscription': "💳 **Subscription**: Free users get basic lessons. Premium unlocks full library, audio, and progress tracking. Upgrade in your profile!",
      'progress': "📊 **Progress Tracking**: View your learning stats in the Progress page. Complete lessons and quizzes to advance!",
      'mobile': "📱 **Mobile Use**: Our app works on phones and tablets. Use Chrome or Safari for best experience.",
      'farsi': "🇮🇷 **Farsi/Persian**: We teach modern Persian (Farsi) as spoken in Iran. Includes cultural context and practical phrases!"
    };

    // Set up auto-responses for keywords
    Object.entries(keywordResponses).forEach(([keyword, response]) => {
      window.$crisp.push(['set', 'session:data', [`keyword_${keyword}`, response]]);
    });
  };

  const setupWelcomeMessage = () => {
    // Set up welcome message for new visitors
    window.$crisp.push(['set', 'session:data', ['welcome_message', {
      enabled: true,
      delay: 3000, // 3 seconds after page load
      message: "سلام! 👋 Welcome to Farsi Learner!\n\nNeed help getting started with Persian? I'm here to assist you! 😊"
    }]]);
  };

  const setupOfflineMessage = () => {
    // Set up offline message when no agents are available
    window.$crisp.push(['set', 'session:data', ['offline_message', {
      enabled: true,
      message: "🌙 **We're currently offline**\n\nOur team will respond within 24 hours. For immediate help:\n\n📧 Email: support@pingquack.com\n💡 Check our FAQ section\n🤖 Use the automated help above\n\nThank you for learning Farsi with us! 📚✨"
    }]]);
  };

  const setupQuickReplies = () => {
    // Set up quick reply buttons for common actions
    window.$crisp.push(['set', 'session:data', ['quick_replies', [
      { title: '🏠 Home', action: 'navigate', url: '/' },
      { title: '📚 Lessons', action: 'navigate', url: '/lessons' },
      { title: '🔤 Alphabet', action: 'navigate', url: '/grid-alphabet' },
      { title: '📊 Progress', action: 'navigate', url: '/progress' },
      { title: '💳 Subscribe', action: 'navigate', url: '/subscription' },
      { title: '❓ FAQ', action: 'navigate', url: '/help' }
    ]]]);
  };

  return null; // This component doesn't render anything
};

export default ChatAutomation;
